import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getAcceptedOrigins } from './challengeConfig';
import { clearCaptchaAccessToken, isCaptchaAccessTokenExpired, setCaptchaAccessToken } from '../../utils/captchaToken';

const PASSED_MESSAGE_TYPE = 'afd-captcha-passed';
const ERROR_MESSAGE_TYPE = 'afd-captcha-error';

/** Return type for the {@link useAfdCaptcha} hook. */
export interface UseAfdCaptchaReturn {
    /** Wraps form submission — shows the challenge modal if token is missing/expired, handles 401/expired-token retry. */
    submit: (fn: () => Promise<void>) => Promise<void>;
    /** Closes the challenge modal and clears any pending submission. */
    hideChallenge: () => void;
    /** Whether the challenge modal is currently visible. */
    isChallengeVisible: boolean;
    /** Resets all CAPTCHA state and increments captchaKey to force iframe remount. */
    reRenderCaptcha: () => void;
    /** Set to true when the stub page reports a WAF misconfiguration (afd-captcha-error). */
    captchaError: boolean;
    /** Monotonically increasing key — use as React key to force iframe remount on retry. */
    captchaKey: number;
}

/** Options for the {@link useAfdCaptcha} hook. */
export interface UseAfdCaptchaOptions {
    /** When false, the hook will not auto-submit the pending form after the CAPTCHA is solved. Defaults to true. */
    autoSubmit?: boolean;
}

/**
 * React hook that manages the AFD WAF CAPTCHA lifecycle.
 *
 * Listens for postMessage events from the CAPTCHA stub iframe, gates form
 * submission behind a valid JWT token, and handles 401 retry by resetting
 * the challenge.
 *
 * @param backendUrl - Backend origin for building the iframe src URL (e.g. env.REACT_APP_BACKEND_URL).
 * @param options - Optional configuration — see {@link UseAfdCaptchaOptions}.
 * @returns CAPTCHA state and controls — see {@link UseAfdCaptchaReturn}.
 */
export function useAfdCaptcha(backendUrl: string, options?: UseAfdCaptchaOptions): UseAfdCaptchaReturn {
    const autoSubmit = options?.autoSubmit ?? true;
    const [captchaPassed, setCaptchaPassed] = useState(false);
    const [isChallengeVisible, setIsChallengeVisible] = useState(false);
    const [captchaKey, setCaptchaKey] = useState(0);
    const [captchaError, setCaptchaError] = useState(false);
    const passedRef = useRef(false);
    const pendingSubmitRef = useRef<(() => Promise<void>) | null>(null);
    const resolvedOrigins = useMemo(() => getAcceptedOrigins(backendUrl), [backendUrl]);

    useEffect(() => {
        const handler = (event: MessageEvent) => {
            if (!resolvedOrigins.includes(event.origin)) return;
            if (event.data?.type === ERROR_MESSAGE_TYPE) { setCaptchaError(true); return; }
            if (event.data?.type !== PASSED_MESSAGE_TYPE) return;
            if (passedRef.current) return;
            passedRef.current = true;

            if (event.data.token) {
                setCaptchaAccessToken(event.data.token, event.data.expiresOn);
            }

            setCaptchaPassed(true);
            setIsChallengeVisible(false);
            setCaptchaError(false);
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [resolvedOrigins]);

    const hideChallenge = useCallback(() => {
        pendingSubmitRef.current = null;
        setIsChallengeVisible(false);
    }, []);

    const reRenderCaptcha = useCallback(() => {
        passedRef.current = false;
        setCaptchaPassed(false);
        setCaptchaError(false);
        clearCaptchaAccessToken();
        setCaptchaKey(k => k + 1);
    }, []);

    const submit = useCallback(async (fn: () => Promise<void>) => {
        if (isCaptchaAccessTokenExpired()) {
            pendingSubmitRef.current = fn;
            reRenderCaptcha();
            setIsChallengeVisible(true);
            return;
        }
        try {
            await fn();
        } catch (error: any) {
            if (error?.response?.status === 401) {
                pendingSubmitRef.current = fn;
                reRenderCaptcha();
                setIsChallengeVisible(true);
                return;
            }
            throw error;
        }
    }, [reRenderCaptcha]);

    useEffect(() => {
        if (autoSubmit && captchaPassed && pendingSubmitRef.current) {
            const fn = pendingSubmitRef.current;
            pendingSubmitRef.current = null;
            void submit(fn).catch(() => { /* handled by submit's throw */ });
        }
    }, [autoSubmit, captchaPassed, submit]);

    return { submit, hideChallenge, isChallengeVisible, reRenderCaptcha, captchaError, captchaKey };
}
